<template>
  <div class="MyEmployeeRequestTable">
    <DataTable
      class="p-datatable-sm"
      :value="employeeRequests"
      :loading="isLoading"
      editMode="cell"
      :rowClass="rowClass"
      :rowHover="true"
      dataKey="number"
      :lazy="true"
      :filters.sync="tableState.filters"
      stateStorage="local"
      :stateKey="filterName"
      filterDisplay="row"
      @page="onPage($event)"
      @sort="onSort($event)"
      @filter="onFilter($event)"
      @state-restore="onStateRestore($event)"
      :totalRecords="getEmployeeRequestCount"
      :paginator="true"
      :rows.sync="tableState.pagination.rowsPerPage"
      :first="pageOffset"
      paginatorTemplate="FirstPageLink PrevPageLink PageLinks NextPageLink LastPageLink CurrentPageReport RowsPerPageDropdown"
      :currentPageReportTemplate="'Anzeigen ' + '{first} bis {last} von {totalRecords} Einträgen'"
      :rowsPerPageOptions="[50, 100, 200]"
    >
      <template #header>
        <div class="table-header">
          <div class="table-header-left"></div>
          <div class="table-header-right">
            <router-link
              class="btn btn-success mb-2 equal-btn"
              :to="{
                name: 'EmployeeRequestCreatePage',
                query: { sourcePage: $route.name },
              }"
            >
              <i class="fa fa-plus-circle mr-2"></i>
              {{ $t('createRequest') }}
            </router-link>
          </div>
        </div>
      </template>
      <template #empty>
        {{ $t('no_data_found') }}
      </template>
      <template #loading>
        {{ $t('loading') }}
      </template>
      <Column
        field="number"
        :header="'#'"
        :styles="{ width: '7%' }"
        :headerStyle="{ width: '60px' }"
        headerClass="center"
        className="center"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.number === '_new'">
            {{ $t('_new') }}
          </span>
          <span v-else>
            <router-link
              :to="{
                name: 'EmployeeRequestEditPage',
                params: { employeeRequestNumber: slotProps.data.number },
                query: { sourcePage: $route.name },
              }"
              >{{ slotProps.data.number.toString().padStart(4, '0') }}</router-link
            >
          </span>
        </template>
      </Column>

      <Column
        field="createdAt"
        :styles="{ width: '10%' }"
        :header="$t('createdAt')"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data.createdAt">
            {{ formatDateWithDots(slotProps.data.createdAt) }}
          </span>
        </template>
      </Column>
      <Column
        field="startTime"
        :header="$t('start')"
        :styles="{ width: '8%' }"
        headerClass="center"
        className="center"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data[slotProps.column.field]">
            {{ formatDateWithDotsNoYear(slotProps.data[slotProps.column.field]) }}
          </span>
        </template>
      </Column>
      <Column
        field="stopTime"
        :header="$t('end')"
        :styles="{ width: '8%' }"
        headerClass="center"
        className="center"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <span v-if="slotProps.data[slotProps.column.field]">
            {{ formatDateWithDotsNoYear(slotProps.data[slotProps.column.field]) }}</span
          >
          <span v-else>
            <HollowDotsSpinner :animation-duration="6000" size="10" color="#495057" />
          </span>
        </template>
      </Column>

      <Column
        field="employeeRequestType"
        :header="$t('type')"
        :styles="{ width: '7%' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge :variant="getEmployeeRequestTypeColor(slotProps.data.employeeRequestType)">
            {{ $t(slotProps.data.employeeRequestType) }}
          </b-badge>
        </template>
      </Column>

      <Column
        field="employeeRequestState"
        :header="$t('state')"
        :styles="{ width: '7%' }"
        :showFilterMenu="false"
      >
        <template #body="slotProps">
          <b-badge :variant="getEmployeeRequestStateColor(slotProps.data.employeeRequestState)">
            {{ $t(slotProps.data.employeeRequestState) }}
          </b-badge>
        </template>
      </Column>
    </DataTable>
  </div>
</template>

<script>
import { mapGetters, mapActions } from 'vuex';
import DataTable from 'primevue/datatable';
import Column from 'primevue/column';
import { FilterMatchMode } from 'primevue/api';
import { HollowDotsSpinner } from 'epic-spinners';

export default {
  name: 'EmployeeRequestTable',
  components: {
    DataTable,
    Column,
    HollowDotsSpinner,
  },
  data() {
    return {
      defaultSorting: {
        field: 'createdAt',
        order: 0,
      },
      defaultPagination: {
        page: 0,
        rowsPerPage: 50,
      },
      defaultFilters: {
        loading: true,
        number: { value: null, matchMode: FilterMatchMode.EQUALS },
        createdAt: { value: null, matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO },
        startTime: {
          value: this.$dayjs()
            .startOf('month')
            .toDate(),
          matchMode: FilterMatchMode.GREATER_THAN_OR_EQUAL_TO,
        },
        stopTime: {
          value: this.$dayjs()
            .endOf('month')
            .toDate(),
          matchMode: FilterMatchMode.LESS_THAN_OR_EQUAL_TO,
        },
        description: { value: null, matchMode: FilterMatchMode.CONTAINS },
      },
      defaultCustomFilters: {
        employeeRequestStates: [],
        employeeRequestTypes: [],
        employeeRequestUserSearch: {
          value: null,
          matchMode: FilterMatchMode.CONTAINS,
        },
      },
      tableState: {
        filterName: 'MyEmployeeRequests-table-filters',
        customFilterName: 'MyEemployeeRequests-custom-table-filters',
        filters: this.defaultFilters,
        customFilters: this.defaultCustomFilters,
        pagination: {
          page: 0,
          rowsPerPage: 50,
        },
        sortField: 'createdAt',
        sortOrder: 0,
      },
    };
  },
  computed: {
    ...mapGetters('auth', ['isAdmin', 'getCurrentUser', 'isClient']),
    ...mapGetters([
      'getEmployeeRequests',
      'getEnumValues',
      'getEmployeeRequestCount',
      'getEnumValues',
      'isLoading',
    ]),
    employeeRequestStates() {
      // To Do: getEnums
      const states = ['UNAUDITED', 'REQUESTED', 'IN_PROGRESS', 'REJECTED', 'APPROVED'];
      return states.map((state) => {
        return { value: state, label: this.$t(state) };
      });
    },
    employeeRequests() {
      if (!this.isAdmin && this.getCurrentUser && this.getEmployeeRequests) {
        return this.getEmployeeRequests.filter(
          (request) => request.user && request.user.id === this.getCurrentUser.id
        );
      }
      return this.getEmployeeRequests;
    },
    showEmployeeColumn() {
      return this.isAdmin;
    },
    employeeRequestTypes() {
      if (!this.getEnumValues('EmployeeRequestType')) {
        return [];
      }
      return this.getEnumValues('EmployeeRequestType').map((type) => {
        return { value: type, label: this.$t(type) };
      });
    },
    pageOffset() {
      return this.tableState.pagination.page * this.tableState.pagination.rowsPerPage;
    },
    filterName() {
      return 'MyEmployeeRequests-table-filters';
    },
    customFilterName() {
      return 'MyEmployeeRequests-custom-table-filters';
    },
  },
  methods: {
    ...mapActions([
      'fetchEnumValues',
      'fetchUserAttachmentUrl',
      'fetchEmployeeRequestsPaginated',
      'createEmployeeRequest',
      'deleteEmployeeRequest',
    ]),
    ...mapActions('auth', ['buildUserEmployeeRequestExport', 'refreshUser']),
    async loadEmployeeRequestData() {
      try {
        const filters = {
          startTime: this.tableState.filters.startTime,
          stopTime: this.tableState.filters.stopTime,
          employeeRequestType: this.tableState.customFilters.employeeRequestTypes,
          employeeRequestStates: this.tableState.customFilters.employeeRequestStates,
          employeeRequestUserSearch: this.tableState.customFilters.employeeRequestUserSearch,
        };

        if (this.getCurrentUser) {
          filters.user = {
            value: this.getCurrentUser.id,
            matchMode: FilterMatchMode.EQUALS,
          };
        }

        await this.fetchEmployeeRequestsPaginated({
          pagination: {
            skip: this.tableState.pagination.page * this.tableState.pagination.rowsPerPage,
            pageSize: this.tableState.pagination.rowsPerPage,
          },
          sorting: {
            sortField: this.tableState.sortField,
            sortOrder: this.tableState.sortOrder,
          },
          filters: filters,
        });
      } catch (error) {
        console.error('Fehler beim Laden der Mitarbeiteranfragen:', error);
      }
    },

    async loadTableData() {
      const filters = {
        startTime: this.tableState.filters.startTime,
        stopTime: this.tableState.filters.stopTime,
      };

      if (this.getCurrentUser) {
        filters.user = {
          value: this.getCurrentUser.id,
          matchMode: FilterMatchMode.EQUALS,
        };

        // console.log('Initial load with user filter:', filters.user);
      }

      await this.fetchEmployeeRequestsPaginated({
        pagination: {
          skip: this.tableState.pagination.page * this.tableState.pagination.rowsPerPage,
          pageSize: this.tableState.pagination.rowsPerPage,
        },
        sorting: {
          sortField: this.tableState.sortField,
          sortOrder: this.tableState.sortOrder,
        },
        filters: filters,
      });
    },
    formatDateWithDots(datetime) {
      if (!datetime) return '';
      const dateObj = new Date(datetime);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      const year = dateObj
        .getFullYear()
        .toString()
        .slice(-2);
      return `${day}.${month}.${year}`;
    },
    formatDateWithDotsNoYear(datetime) {
      if (!datetime) return '';
      const dateObj = new Date(datetime);
      const day = String(dateObj.getDate()).padStart(2, '0');
      const month = String(dateObj.getMonth() + 1).padStart(2, '0');
      return `${day}.${month}`;
    },

    setupFilters() {
      this.tableState.filters = this.tableState.filters
        ? this.tableState.filters
        : this.defaultFilters;
      this.tableState.customFilters = this.tableState.customFilters
        ? this.tableState.customFilters
        : this.defaultCustomFilters;

      this.tableState.pagination.page = 0;
      this.tableState.pagination.rowsPerPage = 50;
      this.tableState.sortField = this.tableState.sortField
        ? this.tableState.sortField
        : 'createdAt';

      this.tableState.sortOrder = this.tableState.sortOrder ? this.tableState.sortOrder : 0;
    },

    async onFilter(event) {
      console.log('Filter event triggered', event);
      await this.loadEmployeeRequestData();
    },

    async onEmployeeRequestStateFilter(value) {
      this.tableState.customFilters.employeeRequestStates = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadEmployeeRequestData();
    },

    async onEmployeeRequestTypeFilter(value) {
      this.tableState.customFilters.employeeRequestTypes = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadEmployeeRequestData();
    },

    async onUserFilter(value) {
      this.tableState.customFilters.employeeRequestUserSearch.value = value;

      localStorage.setItem(
        this.tableState.customFilterName,
        JSON.stringify(this.tableState.customFilters)
      );

      await this.loadEmployeeRequestData();
    },

    async onStateRestore(event) {
      const customFiltersFromStorage = JSON.parse(
        localStorage.getItem(this.tableState.customFilterName)
      );
      this.tableState.customFilters = customFiltersFromStorage
        ? customFiltersFromStorage
        : this.defaultCustomFilters;

      this.setupFilters();
    },
    async onRemoveFilters(event) {
      this.tableState.sorting = this.defaultSorting;
      this.tableState.pagination = this.defaultPagination;
      this.tableState.filters = this.defaultFilters;

      localStorage.removeItem(this.tableState.filterName);
      await this.loadTableData();
    },
    rowClass(data) {
      return data.number === '_new' ? 'new' : null;
    },

    getEmployeeRequestStateColor(state) {
      const stateColors = {
        IN_PROGRESS: 'warning',
        REJECTED: 'danger',
        APPROVED: 'success',
      };
      return stateColors[state] || 'secondary';
    },

    getEmployeeRequestTypeColor(type) {
      const typeColors = {
        VACATION: 'info',
        SICKNESS: 'danger',
        BAD_WEATHER: 'warning',
      };
      return typeColors[type] || 'secondary';
    },

    async onPage(event) {
      this.tableState.pagination.page = event.page;
      this.tableState.pagination.rowsPerPage = event.rows;
      this.loadEmployeeRequestData();
    },

    async onSort(event) {
      this.tableState.sortField = event.sortField;
      this.tableState.sortOrder = event.sortOrder;
      this.loadEmployeeRequestData();
    },
  },

  async created() {
    this.setupFilters();
  },

  async mounted() {
    await this.fetchEnumValues('EmployeeRequestState');
    await this.fetchEnumValues('EmployeeRequestType');
    await this.loadTableData();
  },
};
</script>

<style scoped>
.table-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 1rem;
}

.table-header-left {
  flex: 1;
}

.table-header-right {
  display: flex;
  gap: 0.5rem;
}

.new {
  background-color: rgba(144, 238, 144, 0.2) !important;
}
</style>
